import { Component, EventEmitter, Output } from '@angular/core';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BusinessService } from '../business.service';
import { InfoService } from '@app/core/info/info.service';
import { SessionStorageService } from '@app/core';
import { IBusinessGroup, IBusinessUser, IUserInfoResponse, rolesDictionary } from '../business-models';

@Component({
  selector: 'cloud-business-user',
  templateUrl: './business-user.component.html',
  styleUrls: ['./business-user.component.scss']
})
export class BusinessUserComponent {

  loaded: boolean = false;
  searchHit: boolean = false;
  userForm: FormGroup;
  identifierForm: FormGroup;
  title: string = '';
  editedUser: IBusinessUser;
  newUser: IUserInfoResponse;
  isEdit: boolean = false;
  roles = [];
  selectedRole: string;
  companyCmrId: string;
  allGroups: IBusinessGroup[] = [];

  egn: string = '';
  cmrId: string = '';
  users: IBusinessUser[] = [];


  @Output() close: EventEmitter<any> = new EventEmitter();

  constructor(
    public tableHelper: MatTableHelper,
    public info: InfoService,
    public translateService: TranslateService,
    private sessionStorageService: SessionStorageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private businessService: BusinessService
  ) { }


  ngOnInit() {
    this.getCurrentLoggedInAccount();
    this.title = this.translateService.instant('businessTexts.addUser')
    if (this.data.user) {
      this.title = this.translateService.instant('businessTexts.editUser');
      this.editedUser = this.data.user;
      this.isEdit = true;
      this.selectedRole = this.data.role;
      this.loaded = true;
      this.cmrId = this.editedUser.cmrId;
    }
    this.initIdentifierForm();
    this.initForm();
    this.setRolesArray();
    this.getGroups();
  }

  getGroups() {
    this.businessService.getCompanyGroups(this.companyCmrId).subscribe(res => {
      this.allGroups = res.groups;
    })

  }
  getCurrentLoggedInAccount() {
    this.companyCmrId = this.sessionStorageService.getItem("businessAccountId");
  }

  initForm() {
    this.userForm = this.formBuilder.group({
      name: new FormControl({ value: this.isEdit ? this.getFullName(this.editedUser) : '', disabled: true }, Validators.required),
      position: new FormControl({ value: this.isEdit ? this.editedUser.position : '', disabled: false }, Validators.required),
      email: new FormControl({ value: this.isEdit ? this.editedUser.email : '', disabled: false }, Validators.required),
      phoneNumber: new FormControl({ value: this.isEdit ? this.editedUser.phoneNumber : '', disabled: false }, Validators.required),
      role: new FormControl({ value: this.isEdit ? this.editedUser.role : this.selectedRole, disabled: false }, Validators.required),
      groups: new FormControl({ value: this.isEdit ? this.editedUser.groups : [], disabled: false }, Validators.required),
      cmrId: new FormControl({ value: this.cmrId, disabled: false }),
      companyId: new FormControl({ value: this.isEdit ? this.editedUser.companyId : '', disabled: false })
    });
  }

  initIdentifierForm() {
    this.identifierForm = this.formBuilder.group({
      identifier: new FormControl({ value: this.editedUser ? this.editedUser.personalId : '', disabled: this.isEdit }, Validators.required),
      type: "EGN"
    });
  }

  clearInput() {
    this.identifierForm.get('identifier')?.reset();
    this.loaded = false;
    this.searchHit = false;
  }

  getButtonLabel() {
    return this.isEdit ? this.translateService.instant('businessTexts.edit') : this.translateService.instant('businessTexts.add');
  }

  closeDialog(value) {
    this.close.emit(value);
  }

  onSave() {
    if (this.data.action == 'add') {
      this.businessService.addUser(this.userForm.value, this.companyCmrId).subscribe(res => {
        if (res.status == 200) {
          this.info.success(this.translateService.instant('businessTexts.successAddUser'));
          this.businessService.updateUserTable.next(true);
        } else {
          this.info.error(this.translateService.instant('businessTexts.errorAddUser'));
        }
      })
    } else {
      this.businessService.updateUser(this.userForm.value).subscribe(res => {
        if (res.status == 200) {
          this.info.success(this.translateService.instant('businessTexts.successEditUser'));
          this.businessService.updateUserTable.next(true);
        } else {
          this.info.error(this.translateService.instant('businessTexts.errorEditUser'));
        }
      })
    }
    this.closeDialog(true);
  }


  keyPressNumbers(event) {
    var charCode = (event.which) ? event.which : event.keyCode;
    // Only Numbers 0-9
    if ((charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }


  searchUser() {
    this.searchHit = true;
    if (this.identifierForm.value.identifier?.length) {
      this.businessService.getUserInfo(this.identifierForm.value).subscribe(res => {
        if (res.status == 200) {
          const found = this.data.users.find((obj) => {
            return obj.cmrId === res.cmrId;
          });
          if (found) {
            this.info.error(this.translateService.instant('businessTexts.userAlreadyAdded'));
            this.clearInput();
          } else {
            this.newUser = res;
            this.loaded = true;
            this.cmrId = res.cmrId;
            this.updateForm();
          }
        } else {
          if (res.status == 404) {
            this.info.error(this.translateService.instant('businessTexts.userNotFound'));
            this.searchHit = false;
          }
        }
      });
    }
  }

  updateForm() {
    this.userForm.get(['name']).setValue(this.getFullName(this.newUser));
    this.userForm.get(['email']).setValue(this.newUser.emails[0].email);
    this.userForm.get(['phoneNumber']).setValue(this.newUser.phones[0].number);
    this.userForm.get(['cmrId']).setValue(this.newUser.cmrId);
  }

  setRolesArray() {
    Object.keys(rolesDictionary).forEach(key => {
      this.roles.push(key);
    });
  }

  getRoleName(roleKey) {
    return this.translateService.instant('businessTexts.' + rolesDictionary[roleKey]);
  }

  getName(e) {
    return this.lang == 'bg' ? e.name : e.nameEn;
  }

  getFullName(e) {
    return this.lang == 'bg' ? e.fullNameBg : e.fullNameEn;
  }

  get lang() {
    return this.translateService.currentLang;
  }

  checkInput() {
    return !this.isEdit && this.identifierForm.get(['identifier']).value;
  }

  getTriggerMessage() {
    let message = '';
    if (this.userForm.value.groups.length == 1) {
      message = this.userForm.value.groups[0].name;
    }
    if (this.userForm.value.groups.length > 1) {
      message = this.userForm.value.groups[0].name + ' +' + (this.userForm.value.groups.length - 1) + ' ' + this.translateService.instant('businessTexts.others');
    }
    return message;
  }

  compareObjects(o1: any, o2: any) {
    return o1.id == o2.id;
  }

  onKeyDown(e) {
    this.identifierForm.markAllAsTouched();
    if (e.key === 'Enter' && this.identifierForm.value && !this.loaded) {
      this.searchUser();
    }
  }
}
